import { CheckBox, Pill } from '@iwoca/orion';

import styles from './PayLinkTable.module.css';
import { StatusInformation } from './StatusInformation/StatusInformation';
import { Card } from '../../../../Buyer/components/Card/Card';
import {
  RowAppearance,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../../../../components/Table/Table';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { clipText } from '../../../../utils/ClipText';
import { displayCurrency } from '../../../../utils/DisplayCurrency';
import { getFormattedDate } from '../../../../utils/DisplayDate';
import { PayLinkSource, SellerPayLinks } from '../../util/payments.types';
import { PayLinkStatus } from '../PayLinkStatus/PayLinkStatus';
import { PaymentCard } from '../PaymentCard/PaymentCard';

const sanitiseText = clipText(30);

export const PayLinkTable = ({
  payLinks,
  checkedPayLinksIds,
  setCheckedPayLinksIds,
}: {
  payLinks: SellerPayLinks;
  checkedPayLinksIds: string[];
  setCheckedPayLinksIds: (pay_link_ids: string[]) => void;
}) => {
  const isDesktop = useMediaQuery('(min-width:1024px)');
  const tableData = payLinks.map((payLink) => {
    return {
      id: payLink.id,
      reference: sanitiseText(payLink.reference),
      amount: displayCurrency(payLink.amount),
      dateCreated: getFormattedDate(payLink.created),
      recipient: sanitiseText(
        payLink.buyer_name ? payLink.buyer_name : '-',
      ).toLowerCase(),
      payLinkType: getPayLinkTypeNameFromSource(
        payLink.source as PayLinkSource,
      ),
      status: <PayLinkStatus paylink={payLink} type="expanded" />,
      payLink: payLink,
      ...(payLink.shopify_payment_id && {
        shopify_payment_id: sanitiseText(payLink.shopify_payment_id),
      }),
    };
  });

  const clickCheckbox = (payLinkId: string) => {
    if (checkedPayLinksIds.includes(payLinkId)) {
      const newCheckedPayLinks = checkedPayLinksIds.filter(
        (checkedPayLinkId) => {
          return payLinkId !== checkedPayLinkId;
        },
      );
      setCheckedPayLinksIds(newCheckedPayLinks);
    } else {
      setCheckedPayLinksIds([...checkedPayLinksIds, payLinkId]);
    }
  };
  const isPayLinkChecked = (payLinkId: string) =>
    checkedPayLinksIds.includes(payLinkId);

  return (
    <>
      <div className={styles.payLinkTable} data-testid="pay-links-table">
        {isDesktop ? (
          <Card className={styles.paylinkTableUpper}>
            <Table>
              <TableHead>
                <TableRow>
                  {() => (
                    <>
                      <TableCell></TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Date created</TableCell>
                      <TableCell>Recipient</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>
                        Status
                        <StatusInformation />
                      </TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {tableData.map((row) => (
                  <TableRow
                    key={row.id}
                    defaultRowAppearance={
                      row.payLink.status === 'SCHEDULED_FOR_CAPTURE'
                        ? RowAppearance.HIGHLIGHTED
                        : RowAppearance.NORMAL
                    }
                  >
                    {(setRowAppearance, rowAppearance) => (
                      <>
                        <TableCell>
                          <CheckBox
                            title="Archive checkbox"
                            onChange={() => clickCheckbox(row.id)}
                            checked={checkedPayLinksIds.includes(row.id)}
                            className={styles.archiveCheckbox}
                          />
                        </TableCell>
                        <TableHeaderCell scope="row">
                          <div>{row.reference}</div>
                          {row.payLink.source ===
                            'iwocaPay_shopify_payment_app' && (
                            <div className={styles.shopifyId}>
                              ID: {row.shopify_payment_id}
                            </div>
                          )}
                        </TableHeaderCell>
                        <TableCell>{row.amount}</TableCell>
                        <TableCell>{row.dateCreated}</TableCell>
                        <TableCell>{row.recipient}</TableCell>
                        <TableCell>
                          <Pill>{row.payLinkType}</Pill>
                        </TableCell>
                        <TableCell>
                          <PayLinkStatus
                            paylink={row.payLink}
                            type="expanded"
                            setRowAppearance={setRowAppearance}
                            rowAppearance={rowAppearance}
                          />
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        ) : (
          <div>
            {tableData.map((row) => (
              <PaymentCard
                key={row.id}
                payLinkTableRow={row}
                isPayLinkChecked={isPayLinkChecked}
                clickCheckbox={clickCheckbox}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

const sourceToTypeMap = {
  Xero: 'Xero',
  iwocaPay_pay_link_package: 'Package',
  iwocaPay: 'Custom',
  iwocaPayMe: 'Universal',
  Unknown: 'Unknown',
  iwocaPay_ecommerce_api: 'Ecommerce',
  iwocaPay_woocommerce_extension: 'Woocommerce',
  iwocaPay_magento_extension: 'Magento',
  iwocaPay_quickbooks: 'Quickbooks',
  iwocaPay_shopify_payment_app: 'Shopify',
};

export function getPayLinkTypeNameFromSource(source: PayLinkSource) {
  return sourceToTypeMap[source];
}
